import { useEffect, useMemo } from 'react';
import { Select } from '@teachstonellc/component-library';
import i18n from 'helpers/i18n';
import { CertificationItem } from 'types/api/CertificationItemProductKey';
import messages from './messages';
import { formatCertification, getCertificationSignature } from './utils';
import InfoPopover from './InfoPopover';

interface ChooseChooseLanguageProps {
  certifications: {
    recertification: CertificationItem[];
    transition: CertificationItem[];
  };
  selectedCertification: CertificationItem;
  language?: string;
  updateCertificationLanguage: (language: string) => void;
}

type LanguageOptions = {
  key: string;
  value: string;
  text: string;
};

function ChooseLanguage({
  certifications,
  selectedCertification,
  language = '',
  updateCertificationLanguage
}: ChooseChooseLanguageProps) {
  const languageOptions = useMemo<LanguageOptions[]>(() => {
    const signature = getCertificationSignature(selectedCertification);
    const rows = certifications[selectedCertification.type];
    const languages = rows
      .filter(
        row => !row.disabled && getCertificationSignature(row) === signature
      )
      .map(row => row.language);
    return [...new Set(languages)].map(value => ({
      key: value,
      value,
      text: value
    }));
  }, [certifications, selectedCertification]);

  useEffect(() => {
    if (!languageOptions.some(option => option.value === language)) {
      updateCertificationLanguage(
        languageOptions.length === 1 ? languageOptions[0].value : ''
      );
    }
  }, [languageOptions, language, updateCertificationLanguage]);

  return (
    <div className="rounded bg-white p-4 my-4">
      <div className="mt-4 flex items-center">
        <div className="text-xl font-bold mr-4">{i18n.ft(messages.title)}</div>
      </div>
      <div className="mb-4 text-lg">
        {formatCertification(selectedCertification)}
      </div>
      <div className="my-4">
        <div className="flex items-center">
          <div className="text-xl font-bold mr-4">
            {i18n.ft(messages.video)}
          </div>
          <InfoPopover
            label={i18n.ft(messages.video)}
            content={i18n.ft(messages.info)}
          />
        </div>

        {languageOptions.length <= 1 ? (
          <div className="rounded w-full bg-gray-200 border-2 p-4 mt-1">
            {languageOptions[0]?.text}
          </div>
        ) : (
          <Select
            label={i18n.ft(messages.video)}
            options={languageOptions}
            value={language}
            onChange={value => updateCertificationLanguage(String(value))}
          />
        )}
      </div>
    </div>
  );
}

export default ChooseLanguage;
